import React from 'react';
import type Locale from '@whizz/api-client/dist/@types/model/Locale';
import type { RosterManagementStudentListItem, Tag as TagType, TagsGet } from '@whizz/api-model';
import { Translate, useI18n } from '@whizz/react-i18n';
import exportFromJson from 'export-from-json';
import { Button } from '../../../../components';
import FileUploadForm from './modules/FileUploadForm';
import styled from 'styled-components';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import { GLOBAL_ICONS, QUERIES_IDS, genders } from '../../../../util/vars';
import { useCleverIntegration } from '../../../../hooks/useCleverIntegration';
import { useQuery } from 'react-query';
import { repository } from '@whizz/api-client';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import { DateTime } from 'luxon';
import { range, sortBy } from 'lodash';
import useYearGroups from '../../../../hooks/useYearGroups';

const templateFiles: Record<Locale['id'], string> = {
  'en-gb': 'en-gb/Maths_Whizz_Upload_Template.xlsx',
  'en-ke': 'en-gb/Maths_Whizz_Upload_Template.xlsx',
  'en-us': 'en-us/Math_Whizz_Upload_Template.xlsx',
  'en-nz': 'en-nz/Maths_Whizz_Upload_Template.xlsx',
  'th-th': 'th-th/Maths_Whizz_เทมเพลตเปล่า.xlsx',
};

const studentExamples = [
  {
    firstName: 'EXAMPLE1.FIRST_NAME',
    lastName: 'EXAMPLE1.LAST_NAME',
    upn: 'EXAMPLE1.UPN',
    yearGroup: 'EXAMPLE1.YEAR_GROUP',
    gender: 'EXAMPLE1.GENDER',
    password: 'EXAMPLE1.PASSWORD',
    activate: 'EXAMPLE1.ACTIVATE',
    dob: 'EXAMPLE1.DOB',
    classes: [ 'EXAMPLE1.CLASS' ],
    tags: [ 'SEN' ],
  },
  {
    firstName: 'EXAMPLE2.FIRST_NAME',
    lastName: 'EXAMPLE2.LAST_NAME',
    upn: 'EXAMPLE2.UPN',
    yearGroup: 'EXAMPLE2.YEAR_GROUP',
    gender: 'EXAMPLE2.GENDER',
    password: 'EXAMPLE2.PASSWORD',
    activate: 'EXAMPLE2.ACTIVATE',
    dob: 'EXAMPLE2.DOB',
    classes: [ 'EXAMPLE2.CLASS' ],
    tags: [ '' ],
  },
];

const [
  maxNumberOfClasses,
  maxNumberOfTags,
] = studentExamples
  .reduce((acc, student) => ([
    Math.max(acc[0], student.classes.length),
    Math.max(acc[1], student.tags.length),
  ]), [ 1, 1 ]);

const Guidelines = styled('div')`
  th, td {
    text-align: left;
    border: 1px solid #b7b7b7;
    padding: 0.2rem;
    font-size: 0.75rem;

    @media (min-width: 1024px) {
      padding: 0.3rem;
      font-size: 1rem;
    }
  }
  li {
    & span {
      font-weight: bold;
    }
  }
`;

const TranslateWithSpan = ({ i18nKey }: { i18nKey: string }) => (
  <Translate i18nKey={i18nKey}>
    { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-ignore */ }
    <span />
  </Translate>
);

const Upload = (): JSX.Element => {

  const { locale, translate } = useI18n();
  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();
  const [ isClever ] = useCleverIntegration();

  const onSuccess = () => toggleMessageType('success');
  const onError = () => toggleMessageType('error');

  return (
    <RosterManagementLayout>
      {successErrorModal}
      <div className={'container border-2 border-info rounded-default'}>
        <Guidelines className={'m-md space-y-12'}>
          <BulletSection number={1} title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.DOWNLOAD')}>
            <div className={'space-y-2'}>
              <div className={'flex items-center space-x-2'}>
                <a href={!isClever ? `/downloads/${templateFiles[locale?.id ?? 'en-gb']}` : undefined} download>
                  <Button className={'w-48'} color={'positive'} isOutline icon={GLOBAL_ICONS.download} disabled={isClever}>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK')}</Button>
                </a>
                <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.BLANK_HINT')}</p>
              </div>
              <div className={'flex items-center space-x-2'}>
                <ExportStudents />
                <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.EXISTING_HINT')}</p>
              </div>
            </div>
          </BulletSection>
          <BulletSection number={2} title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.UPDATE')} fullWidth>
            <div className={'space-y-6'}>
              <ul className={'space-y-2 list-disc pl-10'}>
                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.UPN_WITH_HINT'} /></li>
                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.PASSWORD_WITH_HINT'} /></li>
                <li><TranslateWithSpan i18nKey={'pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.TAGS_WITH_HINT'} /></li>
              </ul>
              <div className={'w-full relative overflow-x-auto px-10'}>
                <table className='w-full'>
                  <thead>
                    <tr>
                      <th>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.UPN')}</th>
                      <th>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.FIRST_NAME')}</th>
                      <th>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.LAST_NAME')}</th>
                      <th>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.DOB')}</th>
                      <th>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.YEAR_GROUP')}</th>
                      <th>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.GENDER')}</th>
                      <th>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.PASSWORD')}</th>
                      <th>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.ACTIVATE_STUDENT')}</th>
                      <th colSpan={maxNumberOfClasses}>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.CLASSES')}</th>
                      <th colSpan={maxNumberOfTags}>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.TAGS')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentExamples.map((student) => {
                      const { firstName, lastName, upn, yearGroup, gender, dob, password, activate, classes, tags } = student;
                      return (
                        <tr key={upn}>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${upn}`)}</td>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${firstName}`)}</td>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${lastName}`)}</td>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${dob}`)}</td>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${yearGroup}`)}</td>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${gender}`)}</td>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${password}`)}</td>
                          <td>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${activate}`)}</td>
                          {classes.map((c, index) => (
                            <td key={index}>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${c}`)}</td>
                          ))}
                          {tags.map((c, index) => (
                            <td key={index}>{translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${c}`, { 'defaultValue': c })}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </BulletSection>
          <BulletSection number={3} title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.UPLOAD')}>
            <FileUploadForm onSuccess={onSuccess} onError={onError} />
          </BulletSection>
          <BulletSection number={4} title={translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.SECTIONS.FINISH')}>
            <p>{translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.FINISH')}</p>
          </BulletSection>
        </Guidelines>
      </div>
    </RosterManagementLayout>
  );
};

const BulletSection = ({ number, title, fullWidth = false, children }: { number: number, title: string, children: React.ReactNode, fullWidth?: boolean }) => (
  <div className={'grid items-center'} style={{ gridTemplateColumns: '60px auto' }}>
    <h2 className={'bg-positive rounded-full p-md text-xl mr-md w-12 h-12 flex justify-center items-center font-bold'}>{number}</h2>
    <h2 className={'text-2xl font-bold text-positive'}>{title}</h2>
    <div className={`mt-md ${fullWidth ? 'col-span-2' : 'col-start-2'}`}>{children}</div>
  </div>
);

/** The CSV contains 10 columns for classes and tags. Create those dynamically and insert into the `exportOrder` array. */
const CLASS_TAG_RANGE = 10;
const classes = range(1, CLASS_TAG_RANGE + 1).map((n) => `class${n}`);
const tags = range(1, CLASS_TAG_RANGE + 1).map((n) => `tag${n}`);
type Fields = keyof Pick<RosterManagementStudentListItem, 'upn' | 'firstName' | 'lastName' | 'dob' | 'yearGroupId' | 'gender' | 'active'> | 'delete' & (typeof classes);
const exportOrder = [ 'delete', 'upn', 'firstName', 'lastName', 'dob', 'yearGroupId', 'gender', 'active', ...classes, ...tags ];
const translationKeys = [ 'DELETE', 'UPN', 'FIRST_NAME', 'LAST_NAME', 'DOB', 'YEAR_GROUP', 'GENDER', 'ACTIVATE_STUDENT', 'CLASSES', 'TAGS' ];
const sortByObject = exportOrder.reduce((obj, item, index) => ({ ...obj, [item]: index }), {});

const createRange = <T extends keyof Pick<RosterManagementStudentListItem, 'classes' | 'tags'>>(
  value: RosterManagementStudentListItem[T],
  label: 'class' | 'tag',
) => {
  return range(1, CLASS_TAG_RANGE + 1).reduce((o, n) => {
    const propertyName = label === 'class' ? 'name' : 'abbreviation';
    o[`${label}${n}`] = (value[n - 1] as any)?.[propertyName] ?? '';
    return o;
  }, {} as Record<string, string>);
};

const ExportStudents = () => {
  const { translate } = useI18n();
  const [ isClever ] = useCleverIntegration();
  const tagsQuery = useQuery<TagsGet>(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ],
    () => repository.TagsRepository.getTags(),
    {
      enabled: false,
      placeholderData: [],
    },
  );
  const { data, refetch, remove, isFetching } = useQuery([], () => repository.StudentRepository.rosterManagementListItemsByAction(RosterManagementStudentActionEnum.edit), { enabled: false });
  const [ , getYearGroupById ] = useYearGroups();

  const download = () => {
    if (!isClever) {
      tagsQuery.refetch();
      refetch();
    }
  };

  if (tagsQuery.data && data) {
    const fields = exportOrder;
    const newData = sortBy(
      data.map((value) => {
        const mappedClasses = createRange(value.classes, 'class');
        const translatedTags = value.tags.map((tag) => {
          const apiTag = tagsQuery.data.find((t) => tag.abbreviation === t.abbreviation && t.isDefault);
          if (apiTag) {
            return {
              ...tag,
              abbreviation: translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${tag.abbreviation}`),
            };
          }
          return tag;
        });
        const mappedTags = createRange(translatedTags, 'tag');
        return ({
          ...value,
          delete: translate('pages.ROSTER_MANAGEMENT.UPLOAD.EXPORT.N'),
          upn: value.upn ?? '',
          dob: DateTime.fromISO(value.dob).toLocaleString(),
          active: translate(`pages.ROSTER_MANAGEMENT.UPLOAD.EXPORT.${value.active ? 'Y' : 'N'}`),
          gender: genders[value.gender]?.abbreviation ? translate(`pages.ROSTER_MANAGEMENT.UPLOAD.EXPORT.${genders[value.gender]?.abbreviation}`) : '',
          yearGroupId: value.yearGroupId ? getYearGroupById(value.yearGroupId).code : '',
          ...mappedClasses,
          ...mappedTags,
        });
      }), [ 'class1', 'lastName' ]);

    const beforeTableEncode = (rows: any[]) => {
      let classX = 1;
      let tagX = 1;
      {/**
      * eslint-disable-next-line @typescript-eslint/ban-ts-comment
      * @ts-ignore **/ }
      let newRows = sortBy(rows, (a: { fieldName: Fields }) => sortByObject[a.fieldName]);
      newRows = newRows.map((field: { fieldName: Fields, fieldValues: unknown[] }) => {
        if (field.fieldName.indexOf('class') !== -1) {
          field.fieldName = translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.CLASSES', { x: classX++ });
        } else if (field.fieldName.indexOf('tag') !== -1) {
          field.fieldName = translate('pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.TAGS', { x: tagX++ });
        } else {
          field.fieldName = translate(`pages.ROSTER_MANAGEMENT.UPLOAD.GUIDELINES.${translationKeys[exportOrder.indexOf(field.fieldName)]}`);
        }
        return field;
      });

      /** Insert extra row informing customer to add new students*/
      newRows.forEach((field, index) => {
        newRows[index].fieldValues.push(index === 0 ? translate('pages.ROSTER_MANAGEMENT.UPLOAD.EXPORT.ADD_NEW_STUDENTS_HERE') : '');
      });
      return newRows;
    };
    exportFromJson({ data: newData, fileName: translate('pages.ROSTER_MANAGEMENT.UPLOAD.EXPORT.FILENAME'), exportType: 'csv', withBOM: true, fields, beforeTableEncode });
    remove();
  }

  const loadingData = isFetching;
  return <Button className={'w-48'} color={'positive'} isOutline icon={GLOBAL_ICONS.download} disabled={loadingData || isClever} onClick={download}>{translate(`pages.ROSTER_MANAGEMENT.UPLOAD.BUTTONS.${loadingData ? 'DOWNLOADING' : 'EXISTING'}`)}</Button>;
};

export default Upload;
